import React, { useState, useEffect, useContext } from 'react';
import { Button, MenuItem, InputLabel, Select, TextField, FormControl, Grid, useMediaQuery, Typography, IconButton, Menu, ListItemIcon } from '@mui/material';
import { UserContext, useUserContext } from '../../../context/user_context/UserContext';
import { organizationInventoryOrdersSnapshotListener } from '../../../repository/OrdersDAO';
import { getInventoryItems } from '../../../repository/InventoryDAO';
import { Loading } from '../../../components/loading/Loading';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { Add, FilterAlt, MoreVert } from '@mui/icons-material';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import InventoryOrdersTable from './InventoryOrdersTable';
import AddInventoryOrderModal from './modals/AddInventoryOrderModal';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { TablePaginationKeys } from '../../../components/pagination/TablePaginationKeys';
import { handleChangePage, handleChangeRowsPerPage } from '../../../components/pagination/PaginationHelpers';
import FilterDialog from '../../../components/filter_dialog/FilterDialog';

function InventoryOrders() {
    const { firebaseUser } = useUserContext();
    const [orders, setOrders] = useState();
    const [inventoryItems, setInventoryItems] = useState([]);
    const [showAddOrderModal, setShowAddOrderModal] = useState(false);
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const { suppliers } = useInventoryContext();
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null); 
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem(TablePaginationKeys.InventoryOrderTableRowsPerPage);
        if (savedRowsPerPage) {
            setRowsPerPage(parseInt(savedRowsPerPage, 10));
        }
    }, []);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribeOrders = organizationInventoryOrdersSnapshotListener(firebaseUser.organization, setOrders);
            return () => {
                unsubscribeOrders();
            };
        }
    }, [firebaseUser]);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const items = await getInventoryItems(firebaseUser.organization);
                setInventoryItems(items);
            } catch (error) {
                console.error("Error fetching inventory items:", error);
            }
        };

        fetchItems();
    }, [firebaseUser]);

    const getFilteredOrders = () => {
        return orders
            .filter(order => {
                const supplierMatch = supplierFilter === 'all' || order.supplier.toLowerCase() === supplierFilter.toLowerCase();
                const statusMatch = statusFilter === 'all' || order.status.toLowerCase() === statusFilter.toLowerCase();
                const searchMatch = searchTerm === '' || 
                    order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||  
                    Object.keys(order.items).some(itemId => {
                        const item = inventoryItems.find(i => i.id === itemId);
                        return item && item.name.toLowerCase().includes(searchTerm.toLowerCase());
                    });
    
                return supplierMatch && statusMatch && searchMatch;
            })
            .sort((a, b) => {
                return b.order_date.seconds - a.order_date.seconds; // Newest dates first
            });
    };    

    const handleDeleteOrder = async () => {
        setConfirmOpen(false);
    };

    const getItemNames = (order) => {
        return order.items.map(item => inventoryItems.find(i => i.id === item.inventory_item_id)?.name || 'Unknown Item').join(', ');
    };

    const sortedSuppliers = [...suppliers].sort((a, b) => a.name.localeCompare(b.name));

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenBottomSheet = () => {
        setIsBottomSheetOpen(true);
    };

    const handleCloseBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    return (
        <>
            {orders ? (
                <AppSidebar
                    content={
                        <><Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                    {!isMobile &&
                                        <Grid item>
                                            <Typography variant="h5">{t(StringKeys.ORDERS)}</Typography>
                                        </Grid>}
                                    <Grid item>
                                        <Grid container spacing={2} alignItems="center">
                                            {!isMobile && (
                                                <>
                                                    <Grid item>
                                                        <TextField
                                                            label={t(StringKeys.SEARCH)}
                                                            variant="outlined"
                                                            size="small"
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)} />
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.SORT)}</InputLabel>
                                                            <Select
                                                                value={sortOrder}
                                                                onChange={(e) => setSortOrder(e.target.value)}
                                                                label={t(StringKeys.SORT)}
                                                            >
                                                                <MenuItem value="asc">A-Z</MenuItem>
                                                                <MenuItem value="desc">Z-A</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.SUPPLIER)}</InputLabel>
                                                            <Select
                                                                value={supplierFilter}
                                                                onChange={(e) => setSupplierFilter(e.target.value)}
                                                                label={t(StringKeys.SUPPLIER)}
                                                            >
                                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                {sortedSuppliers.map(supplier => (
                                                                    <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                                                            <Select
                                                                value={statusFilter}
                                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                                label={t(StringKeys.STATUS)}
                                                            >
                                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                <MenuItem value="pending">{t(StringKeys.PENDING)}</MenuItem>
                                                                <MenuItem value="delivered">{t(StringKeys.DELIVERED)}</MenuItem>
                                                                <MenuItem value="canceled">{t(StringKeys.CANCELED)}</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    {hasPermission(Modules.INVENTORY_ORDERS, PermissionTypes.WRITE) && (
                                                        <Grid item>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => setShowAddOrderModal(true)}
                                                                startIcon={<Add />}
                                                            >
                                                                {t(StringKeys.ADD_ORDER)}
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {isMobile &&
                                    <>
                                        <Typography variant="h6">{t(StringKeys.ORDERS)}</Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={10}>
                                                <TextField
                                                    label={t(StringKeys.SEARCH)}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)} />
                                            </Grid>
                                            <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <IconButton onClick={handleMenuClick}>
                                                    <MoreVert />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={Boolean(anchorEl)}
                                                    onClose={handleMenuClose}
                                                >
                                                    <MenuItem onClick={() => { handleOpenBottomSheet(); handleMenuClose(); } }>
                                                        <ListItemIcon>
                                                            <FilterAlt />
                                                        </ListItemIcon>
                                                        {t(StringKeys.FILTER)}
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { setShowAddOrderModal(true); handleMenuClose(); } }>
                                                        <ListItemIcon>
                                                            <Add />
                                                        </ListItemIcon>
                                                        {t(StringKeys.ADD_ORDER)}
                                                    </MenuItem>
                                                </Menu>
                                            </Grid>
                                        </Grid>
                                    </>}
                            </Grid>

                            {/* Inventory Orders Table */}
                            <Grid item xs={12}>
                                <InventoryOrdersTable
                                    orders={getFilteredOrders()}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    handleChangePage={(event, newPage) => handleChangePage(event, newPage, setPage)}
                                    handleChangeRowsPerPage={(event) => handleChangeRowsPerPage(event, TablePaginationKeys.InventoryTableRowsPerPage, setPage, setRowsPerPage)} />
                            </Grid>

                            {/* Add Inventory Order Modal */}
                            <AddInventoryOrderModal
                                show={showAddOrderModal}
                                onHide={() => setShowAddOrderModal(false)}
                                onCreate={() => setShowAddOrderModal(false)}
                                inventoryItems={inventoryItems} />

                            {/* Confirm Action Modal */}
                            <ConfirmDeleteModal
                                open={confirmOpen}
                                onClose={() => setConfirmOpen(false)}
                                onConfirm={handleDeleteOrder}
                                item={orderToDelete && getItemNames(orderToDelete)} />
                        </Grid><FilterDialog
                                open={isBottomSheetOpen}
                                onClose={handleCloseBottomSheet}
                                sortOrder={sortOrder}
                                setSortOrder={setSortOrder}
                                filters={[
                                    <Grid item>
                                        <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                            <InputLabel>{t(StringKeys.SUPPLIER)}</InputLabel>
                                            <Select
                                                value={supplierFilter}
                                                onChange={(e) => setSupplierFilter(e.target.value)}
                                                label={t(StringKeys.SUPPLIER)}
                                            >
                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                {sortedSuppliers.map(supplier => (
                                                    <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>,
                                    <Grid item>
                                        <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                            <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                label={t(StringKeys.STATUS)}
                                            >
                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                <MenuItem value="pending">{t(StringKeys.PENDING)}</MenuItem>
                                                <MenuItem value="delivered">{t(StringKeys.DELIVERED)}</MenuItem>
                                                <MenuItem value="canceled">{t(StringKeys.CANCELED)}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ]} /></>
                    }
                />
            ) : (
                <AppSidebar content={<Loading />} />
            )}
        </>
    );
}

export default InventoryOrders;
